// Identité de marque partagée par tous les PDF (exports tabulaires et
// récapitulatifs de fiches détail) : en-tête (nom + logo) et pied de page
// (coordonnées).
// La marque courante est fournie par l'espace actif (Propriétaire / Agence) via
// PdfBrandProvider ; à défaut, la marque de la plateforme est utilisée.

import type { jsPDF } from "jspdf";
import { BRAND } from "@/config/brand";

export interface PdfBrandContact {
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  ifu?: string;
  rccm?: string;
}

export interface PdfBrand {
  /** Nom de l'agence ou du propriétaire. */
  name: string;
  /** Sous-titre court (ex. « Espace Agence »). */
  subtitle?: string;
  /** Logo encodé en data URL (PNG ou JPEG). */
  logo?: string;
  /** Initiales utilisées en secours quand aucun logo n'est fourni. */
  initials: string;
  /** Coordonnées affichées en pied de page. */
  contact?: PdfBrandContact;
}

/** Construit des initiales (2 lettres) à partir d'un nom. */
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export const DEFAULT_PDF_BRAND: PdfBrand = {
  name: BRAND.name,
  initials: BRAND.initials,
  contact: { email: BRAND.email, phone: BRAND.phone, address: BRAND.address },
};

let currentPdfBrand: PdfBrand = DEFAULT_PDF_BRAND;

/** Définit la marque utilisée par les prochains PDF. */
export function setCurrentPdfBrand(brand: PdfBrand): void {
  currentPdfBrand = brand;
}

/** Résout la marque à utiliser (explicite ou marque courante de l'espace). */
export function resolvePdfBrand(brand?: PdfBrand): PdfBrand {
  return brand ?? currentPdfBrand;
}

// Palette sobre de la charte (tons chauds, sans bleu ni violet).
const BRAND_ACCENT: [number, number, number] = [163, 84, 44];
const BRAND_SOFT: [number, number, number] = [250, 246, 242];
const BRAND_INK: [number, number, number] = [32, 30, 28];
const BRAND_MUTED: [number, number, number] = [126, 118, 111];
const BRAND_LINE: [number, number, number] = [228, 222, 216];

function drawInitialsBadge(doc: jsPDF, brand: PdfBrand, x: number, y: number, size: number): void {
  doc.setFillColor(...BRAND_ACCENT);
  doc.roundedRect(x, y, size, size, 8, 8, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(size * 0.4);
  const initials = (brand.initials || initialsFromName(brand.name)).slice(0, 3);
  doc.text(initials, x + size / 2, y + size / 2 + size * 0.14, { align: "center" });
}

/**
 * Dessine l'en-tête de marque sur la première page et renvoie la position
 * verticale à laquelle le contenu doit commencer.
 *
 * Toutes les informations disponibles du compte sont regroupées ici, empilées
 * sous le nom, dans l'ordre : logo + nom, adresse, téléphone • email, site web,
 * IFU, RCCM. Une ligne dont la donnée est vide n'est pas dessinée.
 */
export function drawPdfHeader(doc: jsPDF, brand: PdfBrand, margin: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - margin * 2;

  const logoSize = 40;
  const logoX = margin;
  const logoY = 16;
  const textX = logoX + logoSize + 12;
  const textMaxWidth = contentWidth - (logoSize + 12);

  const contact = brand.contact ?? {};
  const adresse = (contact.address || "").trim();
  const phone = (contact.phone || "").trim();
  const email = (contact.email || "").trim();
  const website = (contact.website || "").trim();
  const ifu = (contact.ifu || "").trim();
  const rccm = (contact.rccm || "").trim();

  const lines: string[] = [];
  if (adresse) lines.push(adresse);
  const phoneEmail = [phone, email].filter(Boolean).join(" • ");
  if (phoneEmail) lines.push(phoneEmail);
  if (website) lines.push(website);
  if (ifu) lines.push(`IFU : ${ifu}`);
  if (rccm) lines.push(`RCCM : ${rccm}`);

  const nameY = logoY + 12;
  const lineHeight = 10.5;
  const linesTop = nameY + 14;
  const textBottom = lines.length > 0 ? linesTop + (lines.length - 1) * lineHeight + 4 : nameY + 6;
  const bandHeight = Math.max(logoY + logoSize + 12, textBottom + 12);

  // Bandeau de fond + liseré d'accent.
  doc.setFillColor(...BRAND_SOFT);
  doc.rect(0, 0, pageWidth, bandHeight, "F");
  doc.setFillColor(...BRAND_ACCENT);
  doc.rect(0, 0, pageWidth, 5, "F");

  // Logo (proportions conservées) ou badge d'initiales en secours.
  if (brand.logo) {
    try {
      const format = brand.logo.includes("image/png")
        ? "PNG"
        : brand.logo.includes("image/jpeg") || brand.logo.includes("image/jpg")
          ? "JPEG"
          : undefined;
      doc.addImage(brand.logo, format ?? "PNG", logoX, logoY, logoSize, logoSize);
    } catch {
      drawInitialsBadge(doc, brand, logoX, logoY, logoSize);
    }
  } else {
    drawInitialsBadge(doc, brand, logoX, logoY, logoSize);
  }

  // Nom de l'entreprise / agence / propriétaire.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13.5);
  doc.setTextColor(...BRAND_INK);
  doc.text(brand.name || BRAND.name, textX, nameY, { maxWidth: textMaxWidth });

  // Coordonnées empilées, toutes à la suite du nom.
  if (lines.length > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.4);
    doc.setTextColor(...BRAND_MUTED);
    lines.forEach((line, index) => {
      doc.text(line, textX, linesTop + index * lineHeight, { maxWidth: textMaxWidth });
    });
  }

  return bandHeight + 22;
}

/**
 * Dessine le pied de page sur toutes les pages. Le pied ne contient plus que la
 * mention de génération demandée : « Document généré le : … — Propulsé par IMMO BF ».
 * À appeler une fois le contenu entièrement écrit.
 */
export function drawPdfFooter(doc: jsPDF, brand: PdfBrand, margin: number): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();

  for (let page = 1; page <= total; page += 1) {
    doc.setPage(page);
    const footerY = pageHeight - margin + 8;

    doc.setDrawColor(...BRAND_LINE);
    doc.setLineWidth(0.6);
    doc.line(margin, footerY - 14, pageWidth - margin, footerY - 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...BRAND_MUTED);
    doc.text(
      `Document généré le : ${new Date().toLocaleString("fr-FR")} — Propulsé par IMMO BF`,
      pageWidth / 2,
      footerY,
      { align: "center" }
    );
  }
}